import { Col, Flex, Row, Statistic } from 'antd';
import { Result } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import graphContainerStyles from 'business/layout/single-card-page-with-filter-card/graph.module.scss';
import { IndicatorAnalyzeGraph } from 'business/shift-report/indicators/pages/indicator-analyze/components/graph';
import { IndicatorAnalyzeTable } from 'business/shift-report/indicators/pages/indicator-analyze/components/table';
import { getIndicatorName } from 'business/shift-report/indicators/pages/indicator-analyze/get-indicator-name';
import { useIndicatorAnalyzeFilters } from 'business/shift-report/indicators/pages/indicator-analyze/providers/filters';
import { INDICATOR_DISPLAY_TYPES_MAP } from 'business/shift-report/indicators/pages/indicator-analyze/providers/filters/types';
import { useLegendFilters } from 'business/shift-report/indicators/pages/indicator-analyze/providers/legend-filters';
import { getCurrentAnalysisType } from 'business/shift-report/indicators/pages/indicator-analyze/services/get-current-analysis-type';
import { useAvailableIndicatorSelection } from 'business/shift-report/indicators/pages/Indicators/service';
import { useIndicatorV1ControllerGetIndicatorAnalyzeData } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import { getFetchRange } from 'technical/range-filters/get-fetch-range';
import { ContentTitle } from 'ui/content-title';
import Loader from 'ui/loader';
import { ShowMoreToggle } from 'ui/show-more-button';
import { useShowMore } from 'ui/show-more-button/hook';
import { ShowMoreContainer } from 'ui/show-more-button/show-more-container';

import { formatData } from './format-data';

const COL_PROPS = {
  xs: 12,
  sm: 12,
  md: 12,
  lg: 12,
  xl: 8,
};

export const IndicatorAnalyzeDisplayContainer = ({
  showTable = false,
}: {
  showTable?: boolean;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site id');

  const restHeaders = useRestApiHeaders(Module_Enum.ShiftReport);

  const { language } = useTranslation();

  const filters = useIndicatorAnalyzeFilters();
  const { legendFilters } = useLegendFilters();

  const indicators = useAvailableIndicatorSelection(currentConstructionSiteId);

  const analysisType = getCurrentAnalysisType(
    filters.filterType,
    filters.groupByType,
  );

  const fetchRange = getFetchRange(filters);

  const { data, isLoading, isError } =
    useIndicatorV1ControllerGetIndicatorAnalyzeData({
      queryParams: {
        constructionSiteId: currentConstructionSiteId,
        analysisType,
        indicatorIds: indicators.list.map(({ key }) => key),
        selectedIndicatorIds: legendFilters.allIndicators
          ? indicators.list.map(({ key }) => key)
          : legendFilters.selectedIndicators,
        ...fetchRange,
      },
      headers: restHeaders,
    });

  const { isMobile } = useMediaType();
  const { showMore, toggleShowMore } = useShowMore({ total: false });

  if (isError) {
    return <Result status="warning" title={t('errors.error_generic')} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  const formattedData = formatData(data?.values.aggregated, filters);

  const totalItems = data?.values.total ? (
    <Flex vertical>
      {isMobile ? <ContentTitle>{t('common.averages')}</ContentTitle> : null}
      <ShowMoreContainer enableToggle={isMobile} toggleState={showMore.total}>
        <Row gutter={8}>
          {Object.entries(data.values.total).map(([id, total]) => {
            const unit = data.indicators.find((indicator) => {
              return indicator.id === id;
            })?.unit;

            return (
              <Col {...COL_PROPS}>
                <Statistic
                  key={id}
                  title={getIndicatorName(data.indicators, id, language)}
                  value={
                    total
                      ? `${Number(total).toLocaleString(language)} ${unit}`
                      : '-'
                  }
                />
              </Col>
            );
          })}
        </Row>
      </ShowMoreContainer>
      <ShowMoreToggle
        enableToggle={isMobile}
        toggleState={showMore.total}
        toggleFunc={toggleShowMore('total')}
      />
    </Flex>
  ) : null;

  if (filters.displayType === INDICATOR_DISPLAY_TYPES_MAP.table || showTable) {
    return (
      <Flex gap={16} vertical>
        {totalItems}
        <IndicatorAnalyzeTable
          indicatorAnalyzeData={data?.values.aggregated}
          indicatorSeries={data?.indicators}
        />
      </Flex>
    );
  }

  return (
    <Flex gap={16} vertical>
      {totalItems}
      <div className={graphContainerStyles.graphContainer}>
        <IndicatorAnalyzeGraph
          indicators={data?.indicators}
          data={formattedData}
        />
      </div>
    </Flex>
  );
};
